import React, { useMemo, useState } from "react";
import { fuzzy } from "fast-fuzzy";

import Avatar from "@/components/avatar";
import { ItemList, ItemListColumn, ItemListRow } from "@/components/itemList";
import { Badge } from "@/components/ui/badge";
import { usePagination } from "@/hooks/usePagination";
import { formatDate, formatNumber, t } from "@/i18n";

const PAGE_SIZE = 15;

const STATUS_VARIANTS = {
  neutral: "secondary",
  success: "outline-success",
  warning: "warning",
  error: "destructive",
};

const isMissing = (value) => value === null || value === undefined;

const getNumericOptions = (precision) =>
  precision === undefined
    ? undefined
    : {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      };

const formatCellValue = (column, value, currency) => {
  if (isMissing(value)) return "—";

  const numericOptions = getNumericOptions(column.precision);

  switch (column.type) {
    case "number":
      return formatNumber(value, numericOptions);
    case "currency":
      return formatNumber(value, {
        style: "currency",
        currency,
        ...numericOptions,
      });
    case "percent":
      return formatNumber(value / 100, {
        style: "percent",
        ...numericOptions,
      });
    case "date":
      return formatDate(value);
    case "datetime":
      return formatDate(value, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    case "boolean":
      return value ? t("generic.yes") : t("generic.no");
    case "status":
    case "avatar":
      return value.label ?? value.name;
    default:
      return value;
  }
};

const getComparableValue = (column, value) => {
  if (isMissing(value)) return value;

  switch (column.type) {
    case "date":
    case "datetime":
      return new Date(value).getTime();
    case "status":
      return value.label;
    case "avatar":
      return value.name;
    default:
      return value;
  }
};

const compareValues = (first, second) => {
  if (typeof first === "number" && typeof second === "number") {
    return first - second;
  }

  if (typeof first === "boolean" && typeof second === "boolean") {
    return Number(first) - Number(second);
  }

  return String(first).localeCompare(String(second), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const AvatarCell = ({ value }) => {
  if (isMissing(value)) return "—";

  return (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar
        id={value.id}
        name={value.name}
        type={value.avatarType === "company" ? "company" : undefined}
        size="sm"
      />
      <div className="min-w-0">
        <div className="truncate font-semibold" title={value.name}>
          {value.name}
        </div>
        {value.extra && (
          <div
            className="text-muted-foreground truncate text-sm"
            title={value.extra}
          >
            {value.extra}
          </div>
        )}
      </div>
    </div>
  );
};

const Cell = ({ column, value, currency }) => {
  if (column.type === "avatar") {
    return <AvatarCell value={value} />;
  }

  if (column.type === "status" && !isMissing(value)) {
    return (
      <Badge size="sm" variant={STATUS_VARIANTS[value.tone]}>
        {value.label}
      </Badge>
    );
  }

  const formattedValue = formatCellValue(column, value, currency);

  return (
    <span className="truncate" title={String(formattedValue)}>
      {formattedValue}
    </span>
  );
};

const DisplayItemList = ({
  title,
  description,
  emptyText,
  currency,
  columns,
  rows,
}) => {
  const [filtering, setFiltering] = useState({});
  const [sorting, setSorting] = useState();
  const visibleColumns = useMemo(
    () =>
      columns.map((column, columnIndex) => ({
        column,
        columnIndex,
        id: `column-${columnIndex}`,
      })),
    [columns],
  );
  const searchableColumns = useMemo(
    () => visibleColumns.filter(({ column }) => column.searchable),
    [visibleColumns],
  );
  const decoratedRows = useMemo(
    () => rows.map((values, sourceIndex) => ({ key: sourceIndex, values })),
    [rows],
  );

  const filteredAndSortedRows = useMemo(() => {
    const query = filtering.value?.trim().toLowerCase();
    const filteredRows = query
      ? decoratedRows.filter((row) => {
          const searchValue = searchableColumns
            .map(({ column, columnIndex }) =>
              formatCellValue(column, row.values[columnIndex], currency),
            )
            .join(" ")
            .toLowerCase();

          return fuzzy(query, searchValue) > 0.8;
        })
      : decoratedRows;

    if (!sorting?.[0]) return filteredRows;

    const [sortBy, sortDescending] = sorting;
    const sortableColumn = visibleColumns.find(({ id }) => id === sortBy);
    if (!sortableColumn) return filteredRows;
    const { column, columnIndex } = sortableColumn;

    return [...filteredRows].sort((firstRow, secondRow) => {
      const first = getComparableValue(column, firstRow.values[columnIndex]);
      const second = getComparableValue(column, secondRow.values[columnIndex]);

      if (isMissing(first)) return isMissing(second) ? 0 : 1;
      if (isMissing(second)) return -1;

      const comparison = compareValues(first, second);
      return sortDescending ? -comparison : comparison;
    });
  }, [
    currency,
    decoratedRows,
    filtering.value,
    searchableColumns,
    sorting,
    visibleColumns,
  ]);

  const { pageData, paginationElement } = usePagination(filteredAndSortedRows, {
    initialPageSize: PAGE_SIZE,
    disablePageSizeSelector: true,
    pageSizeIntervals: [PAGE_SIZE],
  });

  const itemListColumns = useMemo(
    () =>
      visibleColumns.map(({ column, id }) => ({
        id,
        label: column.label,
        isSortable: column.sortable,
        size: "minmax(0, 1fr)",
        className: ["number", "currency", "percent"].includes(column.type)
          ? "min-w-0 justify-end"
          : "min-w-0",
      })),
    [visibleColumns],
  );

  const canFilter = rows.length > 10 && searchableColumns.length > 0;

  return (
    <section className="bg-background gap-sm pt-sm grid w-full max-w-full min-w-0 rounded-xl to-transparent">
      <h3 className="font-semibold">{title}</h3>
      {description && <p className="text-muted-foreground">{description}</p>}

      <div className="w-full overflow-x-auto">
        <ItemList
          className="min-w-0 text-sm"
          columnClassName="bg-slate-100"
          columns={itemListColumns}
          sorting={sorting}
          onSort={setSorting}
          onFilter={canFilter ? setFiltering : undefined}
          virtualized={false}
          zeroStateRenderer={() => emptyText}
          rightAlignLastColumn
        >
          {pageData.map((row) => (
            <ItemListRow key={row.key}>
              {visibleColumns.map(({ column, columnIndex, id }) => (
                <ItemListColumn
                  key={id}
                  truncate={column.type !== "avatar"}
                  className={
                    ["number", "currency", "percent"].includes(column.type)
                      ? "min-w-0 justify-end"
                      : "min-w-0"
                  }
                >
                  <Cell
                    column={column}
                    value={row.values[columnIndex]}
                    currency={currency}
                  />
                </ItemListColumn>
              ))}
            </ItemListRow>
          ))}
        </ItemList>
      </div>

      {paginationElement}
    </section>
  );
};

export { DisplayItemList };
